import { Dispatch, SetStateAction, useState } from "react";
import { Button, FormGroup, Alert } from "reactstrap";
import { useParams } from "react-router-dom";
import { SelectPicker } from "rsuite";

import { ModuleLayoutSelector } from "../../Drawers/Records/ModuleLayoutSelector";
import { AllViewTypes, ReusableViewIDs } from "../../../types/PageInterfaces";
import { LayoutAndSections, Module } from "../../../types/TenantInterfaces";
import { PageView, PrefillPair } from "../../../types/GlobalInterfaces";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import useAddView from "../../../api/api-hooks/views/useAddView";
import CustomToggle from "../../Drawers/Fields/CustomToggle";
import useUser from "../../../api/api-hooks/auth/useUser";
import ReusableViewSettings from "./ReusableViewSettings";
import FilterInput from "../../SavedFilters/FilterInput";
import ViewSpecificSection from "./ViewSpecificSection";
import AppSpinner from "../../../elements/AppSpinner";
import InfoToolip from "../../../elements/InfoToolip";
import QRScannerSettings from "./QRScannerSettings";
import TenantAPI from "../../../api/api/TenantAPI";
import Constants from "../../../utils/Constants";
import AppDrawer from "../../Drawers/AppDrawer";
import ViewInfoSection from "./ViewInfoSection";
import ViewTypeSection from "./ViewTypeSection";
import StrUtils from "../../../utils/StrUtils";

const defaultFields = Constants.DEFAULT_QUERY_FIELDS;
const DashboardThemes = Object.keys(Constants.DASHBOARD_COLOR_THEMES).map(
  (t) => ({ label: t, value: t })
);

// View types that require a module to be selected.
export const ViewTypesWithModule: any = [
  "Table",
  "Cards",
  "Board",
  "Timeline",
  "Calendar",
];
export const ViewTypesWithField: any = ["Gallery", "RichText", "Subitem"];

export const defaultTableViewSchema = (payload: any, moduleAPIName: string) => {
  payload.ModuleAPIName = moduleAPIName;
  payload.Prefills = [];
  payload.SavedFilters = [];

  const layoutID = StrUtils.uuidv4();

  payload.Query = {
    Fields: defaultFields,
    SavedFilters: [],
    OrderBy: {
      Field: "Created",
      Direction: "DESC",
    },
    Pagination: {
      Count: 25,
      Page: 1,
    },
    _SelectedLayout: payload?.Type === "Table" ? layoutID : "",
  };

  payload.TableLayouts = [
    {
      Name: "Main Layout",
      Fields: defaultFields,
      id: layoutID,
    },
  ];
  payload.CardsLayouts = [];
  payload.BoardLayouts = [];
  payload.TimelineLayouts = [];
};

export const defaultCalendarConfig = (): CalendarConfig => ({
  Fields: [],
  StartField: "",
  EndField: "",
});

const ConfigPages = [
  { name: "View Type", description: "Select the type of View" },
  { name: "View Information", description: "Enter view information" },
  { name: "View Settings", description: "Configure view specific settings" },
  {
    name: "QR Code Scanner Settings",
    description: "Configure QR code scanner",
  },
];

export type CalendarConfig = {
  Fields: string[];
  StartField: string;
  EndField: string;
};

export interface QrCodeConfig {
  SearchField: string;
  FieldsToUpdate: PrefillPair[];
  RecordConditions: any[];
}

export interface BrieGPTConfig {
  ShowAll: boolean;
}

const emptyObjetc = {} as ReusableViewIDs;

const AddViewDrawer = ({
  open,
  handleClose,
  parentViewID = "",
  onAdd,
  isSubView,
  recordModule,
  globalAppID,
  subViews,
  pageID,
}: {
  open: boolean;
  handleClose: () => void;
  globalAppID: string | undefined;
  parentViewID?: string;
  onAdd?: (newID?: string) => void;
  isSubView?: boolean;
  recordModule?: Module;
  subViews?: PageView[];
  pageID?: string;
}) => {
  const { pageID: pageIDParams }: any = useParams();
  const [pageNumber, setPageNumber] = useState(0);
  const { addView, viewAdding, addViewError } = useAddView(
    pageID || pageIDParams
  );

  const [viewName, setViewName] = useState("");
  const [viewDescription, setViewDescription] = useState("");
  const [viewType, setViewType] = useState<AllViewTypes | null>(null);
  const [moduleAPIName, setModuleAPIName] = useState("");
  const [adding, setAdding] = useState(false);
  const [documentPageType, setDocumentPageType] = useState<
    "Single" | "Multiple"
  >("Multiple");
  const [document, setDocument] = useState("");
  const [field, setField] = useState("");
  const [theme, setTheme] = useState("");
  const [insights, setInsights] = useState(false);
  const [link, setLink] = useState("");
  const { user } = useUser();
  const [query, setQuery] = useState({ Filters: [] });
  const [layout, setLayout] = useState<LayoutAndSections | null>(null);
  const [qrCodeConfig, setQrCodeConfig] = useState<QrCodeConfig | null>(null);
  const [calendarConfig, setCalendarConfig] = useState<CalendarConfig>(
    defaultCalendarConfig()
  );
  const [brieGPTConfig, setBrieGPTConfig] = useState<BrieGPTConfig>({
    ShowAll: false,
  });
  // To create Reusable Views in new Views/Subviews
  const [reuseViewID, setReuseViewID] = useState("");
  // @ts-ignore
  const [reusableViews, setReusableViews] =
    useState<ReusableViewIDs>(emptyObjetc);

  // Clean up local state before calling handleClose function.
  const handleOnClose = (newViewID?: string) => {
    setViewName("");
    setViewDescription("");
    setViewType(null);
    setReuseViewID("");
    setModuleAPIName("");
    setDocument("");
    setLink("");
    setField("");
    setTheme("");
    setInsights(false);
    setQuery({ Filters: [] });
    setCalendarConfig({ Fields: [], StartField: "", EndField: "" });
    callFunctionIfExists(onAdd, newViewID);
    setPageNumber(0);
    setQrCodeConfig(null);
    setBrieGPTConfig({ ShowAll: false });
    setReusableViews(emptyObjetc);
    handleClose();
  };

  const handleAdd = async () => {
    setAdding(true);

    const usesViewFilters =
      ViewTypesWithModule.includes(viewType) || viewType === "Dashboard";

    const payload: Record<string, any> = {
      Name: viewName,
      Description: viewDescription,
      Type: viewType,
      ...(usesViewFilters && { ViewFilters: query }),
      ...(layout && { DefaultRecordLayout: layout.id }),
      ...(viewType === "ReuseView" && { ReuseViewID: reuseViewID }),
      ...(Object.keys(reusableViews)?.length && {
        ReusableViewIDs: reusableViews,
      }),
    };

    const handleAddView = () => {
      addView({ payload, parentViewID, onSuccess: handleOnClose });

      setAdding(false);
    };

    if (ViewTypesWithModule.includes(viewType)) {
      if (!moduleAPIName) return setAdding(false);

      if (insights) {
        payload.Insights = true;
        payload.Theme = theme;
      }

      if (qrCodeConfig) {
        payload.QrCodeConfig = qrCodeConfig;
      }

      if (viewType === "Calendar") {
        payload.CalendarConfig = calendarConfig;
      }

      defaultTableViewSchema(payload, moduleAPIName);

      return handleAddView();
    }

    if (viewType === "ReuseView") {
      return handleAddView();
    }

    if (ViewTypesWithField.includes(viewType)) {
      payload.FieldAPIName = field;

      return handleAddView();
    }

    if (viewType === "External") {
      payload.Link = link;

      return handleAddView();
    }

    if (viewType === "Dashboard") {
      payload.Theme = theme;

      return handleAddView();
    }

    if (viewType === "FlowChart") {
      return handleAddView();
    }

    if (viewType === "BrieGPT") {
      payload.ShowAll = brieGPTConfig.ShowAll;
      return handleAddView();
    }

    if (viewType === "Document") {
      payload.DocumentPageType = documentPageType;

      if (documentPageType === "Multiple") {
        return addView({ payload, parentViewID, onSuccess: handleOnClose });
      }

      if (document !== "new") {
        payload.Record = {
          ModuleAPIName: "Documents",
          id: document,
        };

        return handleAddView();
      }

      let documentPayload = {
        Name: payload.Name,
        Owner: {
          ModuleAPIName: "Contacts",
          id: user?.id,
        },
      };

      TenantAPI.insertRecord("Documents", documentPayload)
        .then(
          (data: any) => {
            let record = data?.data?.data?.data;

            payload.Record = {
              ModuleAPIName: "Documents",
              id: record?.id,
            };

            setDocument("");

            return addView({ payload, parentViewID, onSuccess: handleOnClose });
          },
          (e) => {
            console.error(e);
          }
        )
        .finally(() => {
          setAdding(false);
        });
    }
  };

  const canContinue = () => {
    if (viewType === "ReuseView" && reuseViewID) return true;
    if (!viewName || !viewType) return false;
    if (ViewTypesWithModule.includes(viewType) && !moduleAPIName) return false;
    if (viewType === "External" && !link) return false;
    if (viewType === "Calendar" && !calendarConfig.Fields.length) return false;
    if (ViewTypesWithField.includes(viewType) && !field) return false;
    if (qrCodeConfig && !qrCodeConfig.SearchField) return false;
    if (viewType === "Document") {
      if (documentPageType === "Single" && !document) return false;
    }

    return true;
  };

  const isLastPage =
    pageNumber === 3 ||
    (pageNumber === 2 && !qrCodeConfig) ||
    (pageNumber === 1 && viewType === "FlowChart");

  const handleNextPage = () => {
    if (isLastPage) return handleAdd();
    let number = 1;

    if (viewType === "ReuseView") {
      number = 2;
    }

    setPageNumber((prev) => prev + number);
  };

  const handlePreviousPage = () => {
    let number = 1;

    if (viewType === "ReuseView") {
      number = 2;
    }

    setPageNumber((prev) => prev - number);
  };

  const page = ConfigPages[pageNumber];

  if (!page) return null;

  return (
    <AppDrawer
      open={open}
      onClose={handleOnClose}
      headerTitle={"View Creation"}
      headerDescription={
        <div>
          <span className="text-dark font-weight-600">
            Step {viewType === "ReuseView" && isLastPage ? 2 : pageNumber + 1}:
          </span>{" "}
          {page.description}
        </div>
      }
      bodyCSS="pt-2 pb-8"
      headerOptions={
        <>
          {pageNumber !== 0 && (
            <Button size="sm" onClick={handlePreviousPage}>
              Previous
            </Button>
          )}
          <Button
            color="primary"
            size="sm"
            style={{ width: 80 }}
            disabled={
              (isLastPage && canContinue() === false) || adding || viewAdding
            }
            onClick={handleNextPage}
          >
            {isLastPage ? (
              adding || viewAdding ? (
                <AppSpinner color="white" />
              ) : (
                "Add"
              )
            ) : (
              "Next"
            )}
          </Button>
        </>
      }
      body={(containerRef) => {
        return (
          <>
            {page.name === "View Type" && (
              <ViewTypeSection
                viewType={viewType}
                setViewType={setViewType}
                setField={setField}
                isSubView={isSubView}
                setPageNumber={setPageNumber}
                globalAppID={globalAppID}
              />
            )}
            {page.name === "View Information" && (
              <ViewInfoSection
                name={viewName}
                setName={setViewName}
                description={viewDescription}
                setDescription={setViewDescription}
                setPageNumber={setPageNumber}
              />
            )}
            {page.name === "View Settings" && (
              <ViewSpecificSection
                containerRef={containerRef}
                viewType={viewType!}
                moduleAPIName={moduleAPIName}
                setModuleAPIName={setModuleAPIName}
                field={field}
                setField={setField}
                link={link}
                setLink={setLink}
                query={query}
                setQuery={setQuery}
                theme={theme}
                setTheme={setTheme}
                insights={insights}
                setInsights={setInsights}
                recordModule={recordModule}
                calendarConfig={calendarConfig}
                setCalendarConfig={setCalendarConfig}
                layout={layout}
                setLayout={setLayout}
                documentPageType={documentPageType}
                setDocumentPageType={setDocumentPageType}
                document={document}
                setDocument={setDocument}
                qrCodeConfig={qrCodeConfig}
                setQrCodeConfig={setQrCodeConfig}
                brieGPTConfig={brieGPTConfig}
                setBrieGPTConfig={setBrieGPTConfig}
                globalAppID={globalAppID}
                setReuseViewID={setReuseViewID}
                reuseViewID={reuseViewID}
                pageID={pageID || pageIDParams}
                parentViewID={parentViewID}
                subViews={subViews}
                reusableViews={reusableViews}
                setReusableViews={setReusableViews}
              />
            )}
            {page.name === "QR Code Scanner Settings" && qrCodeConfig && (
              <QRScannerSettings
                moduleAPIName={moduleAPIName}
                qrCodeConfig={qrCodeConfig}
                setQrCodeConfig={setQrCodeConfig}
              />
            )}
            {addViewError && (
              <Alert color="danger" className="mt-4">
                {addViewError}
              </Alert>
            )}
          </>
        );
      }}
    />
  );
};

export function ViewTypesWithModuleSection({
  query,
  setQuery,
  moduleAPIName,
  module,
  layout,
  setLayout,
  insights,
  setInsights,
  qrCodeConfig,
  setQrCodeConfig,
  showQrConfig,
  globalAppID,
  viewID,
  reusableViews,
  setReusableViews,
  disabled,
}: {
  query: any;
  setQuery: any;
  moduleAPIName: string;
  module: Module;
  layout: LayoutAndSections | null;
  setLayout: Dispatch<SetStateAction<LayoutAndSections | null>>;
  insights: boolean;
  setInsights: Dispatch<SetStateAction<boolean>>;
  qrCodeConfig: QrCodeConfig | null;
  setQrCodeConfig: Dispatch<SetStateAction<QrCodeConfig | null>>;
  showQrConfig?: boolean;
  globalAppID: string | undefined;
  viewID: string | undefined;
  reusableViews: ReusableViewIDs | undefined;
  setReusableViews: Dispatch<SetStateAction<ReusableViewIDs>> | undefined;
  disabled?: boolean;
}) {
  return (
    <>
      <h4 className="mb-0 mt-4">
        View Filter
        <InfoToolip
          iconCSS="ml-2"
          placement="right"
          text={
            <>
              This is the base filter for this view. <br /> <br />
              Any separate filters added later on will be merged with this
              filter.
            </>
          }
        />
      </h4>
      <FilterInput
        disabled={disabled}
        currentModule={module}
        setQuery={(query: any) => setQuery(query)}
        query={query}
        fallbackMessage="No Filter Added."
        smallText="Click the box above to create a Filter."
        moduleAPIName={moduleAPIName}
        showVariables={true}
        checkForFields={false}
        // @ts-ignore
        onClear={() => handleRemoveFilter()}
      />
      <h4 className="mt-4 mb--1">
        Default Record Layout
        <InfoToolip
          iconCSS="ml-2"
          placement="right"
          text={
            <>
              This allows you to override the default record layout for this
              data source.
              <br />
              <br />
              Any time you are in this view, the layout you select here will be
              shown when viewing records.
            </>
          }
        />
      </h4>
      <ModuleLayoutSelector
        disabled={disabled}
        module={module}
        layout={layout}
        setLayout={(val) => setLayout(val)}
        block
      />
      <CustomToggle
        containerCSS="mt-2"
        title="Insights"
        description="Enable Dashboard Widgets alongside this view"
        stateName="Insights"
        value={insights}
        handleChange={(e: any) => setInsights(e.target.checked)}
        disabled={disabled}
      />
      <CustomToggle
        containerCSS="mt-2"
        title="QR Code Scanner"
        description="Adds the ability to scan QR codes in this view. Configuration for this feature is in the next step if you enable it."
        stateName="QRScanner"
        value={!!qrCodeConfig}
        disabled={disabled}
        handleChange={(e: any) =>
          setQrCodeConfig(
            e.target.checked
              ? { SearchField: "", FieldsToUpdate: [], RecordConditions: [] }
              : null
          )
        }
      />
      {qrCodeConfig && showQrConfig && (
        <QRScannerSettings
          moduleAPIName={moduleAPIName}
          qrCodeConfig={qrCodeConfig}
          setQrCodeConfig={setQrCodeConfig}
          disabled={disabled}
        />
      )}
      {globalAppID && setReusableViews && !disabled && (
        <ReusableViewSettings
          globalAppID={globalAppID}
          viewID={viewID}
          moduleAPIName={moduleAPIName}
          reusableViews={reusableViews}
          setReusableViews={setReusableViews}
          module={module}
        />
      )}
    </>
  );

  function handleRemoveFilter() {
    setQuery((prev: any) => ({ ...(prev || {}), Filters: [] }));
  }
}

export function ThemeSelector({
  theme,
  setTheme,
}: {
  theme: string;
  setTheme: Dispatch<SetStateAction<string>>;
}) {
  return (
    <FormGroup>
      <h4>Dashboard Theme</h4>
      <SelectPicker
        searchable={false}
        data={DashboardThemes}
        value={theme}
        onChange={(t) => setTheme(t || "")}
        onClean={() => setTheme("")}
        preventOverflow
        block
        placeholder="Select Theme"
        renderMenuItem={(label) => {
          const theme = Constants.DASHBOARD_COLOR_THEMES[label as "Default"]; // Satisfy typescript
          const slicedTheme = theme.length > 11 ? theme.slice(0, 11) : theme;

          return (
            <div>
              <h4 className="mb-0">{label}</h4>
              <div className="d-flex">
                {slicedTheme.map((color) => (
                  <div
                    key={color}
                    style={{
                      backgroundColor: color,
                      width: 20,
                      height: 20,
                    }}
                  ></div>
                ))}
              </div>
            </div>
          );
        }}
      />
    </FormGroup>
  );
}

export default AddViewDrawer;
